import { Autocomplete, Chip, Grid, List, TextField, useTheme } from '@mui/material'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { formatDayJsTime } from 'utils/formatDayjsDateTime'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { determineStaleTask } from './determineStaleTask'
import { getTaskCommentPrefix } from './utils/getTaskCommentPrefix'

const TaskSelector = ({ checklistItems, onTaskSelected, selectedTask, taskComments, commentCounts }) => {
  const theme = useTheme()
  const processedChecklistItems = useMemo(() => {
    if (!checklistItems || !checklistItems?.length) return []
    return (checklistItems ?? [])
      ?.map(item => {
        const prefix = getTaskCommentPrefix(item.SK) // TASK or SUBTASK
        const fullKey = `${prefix}_${item.PK}_${item.SK}`

        // 🟡 NEW: Check by fullKey or fallback to just SK
        const commentArray = taskComments?.[fullKey] || taskComments?.[item.SK] || []
        const commentCount = commentArray.length
        const isStaleOptimization = item?.SK?.startsWith('OPTIMIZATION')
          ? determineStaleTask(item.timeCreated || item.createdTime || item.createdAt)
          : false
        const isStaleMoTask = item?.SK?.startsWith('MONTHLY_TASK_')
          ? determineStaleTask(item.timeCreated || item.createdTime || item.createdAt)
          : false
        const isStaleTask = isStaleOptimization || isStaleMoTask || false
        return item
          ? {
              ...item,
              isStaleTask,
              identifier: item?.PK?.startsWith('OPTIMIZATION_')
                ? 'Optimization'
                : item?.role === 'AM'
                ? 'AM Task'
                : item?.role === 'DMS'
                ? 'DMS Task'
                : item?.SK?.startsWith('SUBTASK')
                ? 'Subtask'
                : item?.SK.startsWith('MONTHLY_TASK_')
                ? 'Monthly Task'
                : 'Task',
              idColor: item?.SK?.startsWith('OPTIMIZATION_')
                ? 'rgb(65, 190, 169)'
                : item?.role === 'AM' || item?.role === 'DMS'
                ? 'rgb(251, 193, 30)'
                : item?.SK?.startsWith('SUBTASK')
                ? 'rgb(104, 202, 238)'
                : item?.SK.startsWith('MONTHLY_TASK_')
                ? 'rgb(20, 119, 248)'
                : 'rgb(231, 102, 223)',
              mtbIdentifier:
                item?.SK.startsWith('MONTHLY_TASK_') && item?.isSeoMonthlyTask
                  ? 'SEO Mo. Task'
                  : item?.SK.startsWith('MONTHLY_TASK_') && item?.isDmsMonthlyTask
                  ? 'DMS Mo. Task'
                  : item?.SK.startsWith('MONTHLY_TASK_') && item?.isAmMonthlyTask
                  ? 'AM Mo. Task'
                  : item?.SK?.startsWith('OPTIMIZATION_')
                  ? 'Optimization'
                  : item?.taskType
                  ? formatCamelCase(item?.taskType)
                  : item?.type
                  ? formatCamelCase(item?.type)
                  : 'Task',
              commentCount,
            }
          : null
      })
      ?.sort((a, b) => {
        // Put non-stale tasks before stale ones
        if (a.isStaleTask && !b.isStaleTask) return 1
        if (!a.isStaleTask && b.isStaleTask) return -1

        // Then sort by creation time (descending)
        const aTime = new Date(a.timeCreated || a.createdTime || 0).getTime()
        const bTime = new Date(b.timeCreated || b.createdTime || 0).getTime()
        return bTime - aTime
      })
  }, [checklistItems])
  return (
    <Autocomplete
      value={selectedTask}
      options={processedChecklistItems}
      getOptionLabel={option => option.name || option.title || ''}
      onChange={(event, newValue) => onTaskSelected(newValue)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={params => (
        <TextField
          {...params}
          label="Select Task"
          variant="outlined"
          sx={{
            '& .MuiInputBase-input': { color: theme.palette.text.primary },
            '& .MuiInputLabel-root': { color: theme.palette.text.primary },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.background.scrollbar_thumb,
            },
          }}
        />
      )}
      renderOption={(props, option, { index }) => (
        <List
          {...(props as any)}
          key={`${option?.SK || option?.id || index}`}
          sx={{
            width: '100%',
            opacity: option?.isStaleTask ? 0.5 : 1,
            pointerEvents: option?.isStaleTask ? 'none' : 'auto',
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between" width="100%">
            <Grid item xs={5}>
              {option.name || option.title}
            </Grid>
            {(option?.timeCreated || option?.createdTime || option?.createdAt) && (
              <Grid item xs={4} sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
                {/* {formatDayJsTime(option?.timeCreated || option?.createdTime || option?.createdAt)} */}
                {`${formatDayJsTime(option?.timeCreated || option?.createdTime || option?.createdAt)} • ${dayjs(
                  option?.timeCreated || option?.createdTime || option?.createdAt
                ).format('MMMM DD, YYYY')}`}
              </Grid>
            )}

            {option?.mtbIdentifier ? (
              <Grid item xs={2} sx={{ color: renderColorByString(option?.mtbIdentifier), fontSize: 14 }}>
                {option?.mtbIdentifier}
              </Grid>
            ) : option?.SK?.startsWith('OPTIMIZATION') ? (
              <Grid item xs={2} sx={{ color: renderColorByString(option?.mtbIdentifier), fontSize: 14 }}>
                {option?.identifier ?? option?.mtbIdentifier}
              </Grid>
            ) : (
              <Grid item xs={2} sx={{ color: option?.idColor, fontSize: 14 }}>
                {option?.identifier}
              </Grid>
            )}
            <Grid item xs={1}>
              {option?.SK && commentCounts?.[option.SK] > 0 && (
                <Chip size="small" label={commentCounts[option.SK]} sx={{ color: 'rgb(24, 144, 255)', fontSize: 18 }} />
              )}
            </Grid>
          </Grid>
        </List>
      )}
      ListboxProps={{
        sx: {
          // set width of scrollbar if needed:
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.background.scrollbar_thumb,
            borderRadius: 4,
          },
        },
      }}
    />
  )
}

export default TaskSelector
